// Modifications d'un brouillon par le Studio (lot 5) : ajouter, dupliquer,
// déplacer, supprimer des scènes, des objets et des étapes ; changer un
// champ. Pur, sur le brouillon BRUT (tel qu'en base), toujours une copie ;
// la validation dit ensuite ce qui ne va pas (le Studio la montre).

import type { JrafAction } from './schema/steps';
import type { JrafObject } from './schema/objects';

type Json = Record<string, unknown>;
export type DraftPath = readonly (string | number)[];

function isRecord(v: unknown): v is Json {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asDoc(draft: unknown): Json {
  if (!isRecord(draft)) throw new Error('brouillon illisible');
  return structuredClone(draft);
}

function list(doc: Json, key: 'scenes' | 'objects'): Json[] {
  if (!Array.isArray(doc[key])) doc[key] = [];
  return doc[key] as Json[];
}

/** Valeur au chemin donné (`['scenes', 2, 'narration', 'text']`), ou `undefined`. */
export function getAt(doc: unknown, path: DraftPath): unknown {
  let cur: unknown = doc;
  for (const k of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[k];
  }
  return cur;
}

/**
 * Copie du brouillon avec `value` au chemin donné ; `undefined` retire le
 * champ (un objet vidé par là disparaît aussi : `style: {}` → pas de style).
 */
export function setAt(draft: unknown, path: DraftPath, value: unknown): Json {
  const doc = asDoc(draft);
  if (path.length === 0) throw new Error('chemin vide');
  const parents: unknown[] = [doc];
  let cur: unknown = doc;
  for (const k of path.slice(0, -1)) {
    const next = (cur as Record<string | number, unknown>)[k];
    if (next === null || typeof next !== 'object') {
      if (value === undefined) return doc; // rien à retirer
      const created = {};
      (cur as Record<string | number, unknown>)[k] = created;
      cur = created;
    } else cur = next;
    parents.push(cur);
  }
  const last = path[path.length - 1]!;
  if (value === undefined) {
    if (Array.isArray(cur) && typeof last === 'number') cur.splice(last, 1);
    else delete (cur as Record<string | number, unknown>)[last];
    // sous-objets vidés : retirés (de bas en haut, jamais le document)
    for (let i = path.length - 2; i >= 0; i--) {
      const holder = parents[i] as Record<string | number, unknown>;
      const child = holder[path[i]!];
      if (isRecord(child) && Object.keys(child).length === 0 && i > 0) delete holder[path[i]!];
      else break;
    }
  } else (cur as Record<string | number, unknown>)[last] = structuredClone(value);
  return doc;
}

/** Identifiant libre : `base`, sinon `base-2`, `base-3`… (40 caractères au plus). */
export function uniqueId(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const clean =
    base
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 36) || 'element';
  if (!used.has(clean)) return clean;
  for (let k = 2; k < 10_000; k++) {
    const id = `${clean.slice(0, 40 - String(k).length - 1)}-${k}`;
    if (!used.has(id)) return id;
  }
  throw new Error('identifiants épuisés');
}

const idsOf = (items: readonly Json[]) =>
  items.flatMap((x) => (typeof x.id === 'string' ? [x.id] : []));

function sceneAt(doc: Json, sceneId: string): { scene: Json; index: number } {
  const scenes = list(doc, 'scenes');
  const index = scenes.findIndex((s) => s.id === sceneId);
  if (index < 0) throw new Error(`scène « ${sceneId} » introuvable`);
  return { scene: scenes[index]!, index };
}

// -------------------------------------------------------------- scènes

/** Nouvelle scène (après `afterIndex`, à la fin sinon), avant les scènes « À toi ». */
export function addScene(draft: unknown, afterIndex?: number): { doc: Json; id: string } {
  const doc = asDoc(draft);
  const scenes = list(doc, 'scenes');
  const id = uniqueId('scene', idsOf(scenes));
  const firstInteractive = scenes.findIndex((s) => s.interactive === true);
  let at = afterIndex === undefined ? scenes.length : afterIndex + 1;
  // les scènes « À toi » restent à la fin (règle du format)
  if (firstInteractive >= 0) at = Math.min(at, firstInteractive);
  scenes.splice(Math.max(0, at), 0, {
    id,
    title: 'Nouvelle scène',
    narration: { text: 'À écrire.' },
    steps: [],
  });
  return { doc, id };
}

/** Copie d'une scène, juste après elle (mêmes objets, mêmes étapes). */
export function duplicateScene(draft: unknown, sceneId: string): { doc: Json; id: string } {
  const doc = asDoc(draft);
  const { scene, index } = sceneAt(doc, sceneId);
  const scenes = list(doc, 'scenes');
  const id = uniqueId(`${sceneId}-copie`, idsOf(scenes));
  const copy = structuredClone(scene);
  copy.id = id;
  if (typeof copy.title === 'string') copy.title = `${copy.title} (copie)`.slice(0, 80);
  // la voix enregistrée dit l'autre scène : la copie a la voix du navigateur
  if (isRecord(copy.narration)) delete copy.narration.audioAssetId;
  scenes.splice(index + 1, 0, copy);
  return { doc, id };
}

/** Monte (-1) ou descend (+1) une scène. */
export function moveScene(draft: unknown, sceneId: string, delta: -1 | 1): Json {
  const doc = asDoc(draft);
  const { index } = sceneAt(doc, sceneId);
  const scenes = list(doc, 'scenes');
  const to = index + delta;
  if (to < 0 || to >= scenes.length) return doc;
  const [s] = scenes.splice(index, 1);
  scenes.splice(to, 0, s!);
  return doc;
}

export function removeScene(draft: unknown, sceneId: string): Json {
  const doc = asDoc(draft);
  const { index } = sceneAt(doc, sceneId);
  list(doc, 'scenes').splice(index, 1);
  return doc;
}

// -------------------------------------------------------------- objets

/** Modèle d'un nouvel objet : au centre, caché au départ (une étape le montre). */
export function objectTemplate(
  type: Exclude<JrafObject['type'], 'image'>,
  id: string,
  canvas: { width: number; height: number } = { width: 360, height: 640 },
): Json {
  const cx = Math.round(canvas.width / 2);
  const cy = Math.round(canvas.height / 2);
  const hidden = { initial: { visible: false } };
  switch (type) {
    case 'rect':
      return {
        id,
        type,
        x: cx - 50,
        y: cy - 40,
        width: 100,
        height: 80,
        rx: 8,
        style: { fill: 'primary' },
        ...hidden,
      };
    case 'circle':
      return { id, type, cx, cy, r: 40, style: { stroke: 'paper', strokeWidth: 3 }, ...hidden };
    case 'ellipse':
      return {
        id,
        type,
        cx,
        cy,
        rx: 60,
        ry: 36,
        style: { stroke: 'paper', strokeWidth: 3 },
        ...hidden,
      };
    case 'line':
      return {
        id,
        type,
        x1: cx - 80,
        y1: cy,
        x2: cx + 80,
        y2: cy,
        style: { stroke: 'paper', strokeWidth: 3 },
        ...hidden,
      };
    case 'arrow':
      return {
        id,
        type,
        x1: cx - 80,
        y1: cy,
        x2: cx + 80,
        y2: cy,
        style: { stroke: 'gold', strokeWidth: 3 },
        ...hidden,
      };
    case 'polygon':
      return {
        id,
        type,
        points: [
          { x: cx - 60, y: cy + 50 },
          { x: cx + 60, y: cy + 50 },
          { x: cx - 60, y: cy - 50 },
        ],
        style: { stroke: 'paper', strokeWidth: 3 },
        ...hidden,
      };
    case 'path':
      return {
        id,
        type,
        d: `M ${cx - 80} ${cy} Q ${cx} ${cy - 80} ${cx + 80} ${cy}`,
        style: { stroke: 'paper', strokeWidth: 3 },
        ...hidden,
      };
    case 'point':
      return { id, type, x: cx, y: cy, label: 'A', ...hidden };
    case 'text':
      return { id, type, at: { x: cx, y: cy }, text: 'Nouveau texte', size: 20, ...hidden };
    case 'formula':
      return { id, type, at: { x: cx, y: cy }, tex: 'a + b = c', size: 26, ...hidden };
    case 'value':
      return { id, type, at: { x: cx, y: cy }, expr: 0, size: 22, ...hidden };
    case 'group':
      return { id, type, x: 0, y: 0 };
  }
}

const TYPE_ID: Record<string, string> = {
  rect: 'cadre',
  circle: 'cercle',
  ellipse: 'ellipse',
  line: 'segment',
  arrow: 'fleche',
  polygon: 'polygone',
  path: 'trace',
  point: 'point',
  text: 'texte',
  formula: 'formule',
  value: 'nombre',
  group: 'groupe',
};

export function addObject(
  draft: unknown,
  type: Exclude<JrafObject['type'], 'image'>,
): { doc: Json; id: string } {
  const doc = asDoc(draft);
  const objects = list(doc, 'objects');
  const id = uniqueId(TYPE_ID[type] ?? type, idsOf(objects));
  const canvas = isRecord(doc.canvas)
    ? { width: Number(doc.canvas.width) || 360, height: Number(doc.canvas.height) || 640 }
    : undefined;
  objects.push(objectTemplate(type, id, canvas));
  return { doc, id };
}

/** Copie d'un objet, décalée de 16 px quand sa position est un nombre. */
export function duplicateObject(draft: unknown, objectId: string): { doc: Json; id: string } {
  const doc = asDoc(draft);
  const objects = list(doc, 'objects');
  const index = objects.findIndex((o) => o.id === objectId);
  if (index < 0) throw new Error(`objet « ${objectId} » introuvable`);
  const id = uniqueId(`${objectId}-copie`, idsOf(objects));
  const copy = structuredClone(objects[index]!);
  copy.id = id;
  const shift = (holder: Json, key: string) => {
    if (typeof holder[key] === 'number') holder[key] = (holder[key] as number) + 16;
  };
  for (const k of ['x', 'y', 'cx', 'cy', 'x1', 'y1', 'x2', 'y2']) shift(copy, k);
  if (isRecord(copy.at)) {
    shift(copy.at, 'x');
    shift(copy.at, 'y');
  }
  objects.splice(index + 1, 0, copy);
  return { doc, id };
}

/**
 * Supprime un objet : retiré des étapes qui le visent (une étape qui ne vise
 * plus rien disparaît, et ce qui démarrait `with` elle démarre à sa place),
 * ses enfants sortent du groupe.
 */
export function removeObject(draft: unknown, objectId: string): Json {
  const doc = asDoc(draft);
  doc.objects = list(doc, 'objects').filter((o) => o.id !== objectId);
  for (const o of doc.objects as Json[]) if (o.parent === objectId) delete o.parent;
  for (const s of list(doc, 'scenes')) {
    const steps = Array.isArray(s.steps) ? (s.steps as Json[]) : [];
    const removed = new Set<string>();
    s.steps = steps.filter((st) => {
      const t = st.target;
      if (t === objectId) {
        removed.add(String(st.id));
        return false;
      }
      if (Array.isArray(t)) {
        const rest = t.filter((x) => x !== objectId);
        if (rest.length === 0) {
          removed.add(String(st.id));
          return false;
        }
        st.target = rest.length === 1 ? rest[0] : rest;
      }
      return true;
    });
    for (const st of s.steps as Json[]) if (removed.has(String(st.with))) delete st.with;
  }
  return doc;
}

/** Scènes où chaque objet est visé par une étape. */
export function objectUsage(draft: unknown): Map<string, string[]> {
  const out = new Map<string, string[]>();
  if (!isRecord(draft)) return out;
  for (const s of Array.isArray(draft.scenes) ? (draft.scenes as Json[]) : []) {
    for (const st of Array.isArray(s.steps) ? (s.steps as Json[]) : []) {
      const t = st.target;
      for (const id of Array.isArray(t) ? t : [t]) {
        if (typeof id !== 'string') continue;
        const scenes = out.get(id) ?? [];
        if (!scenes.includes(String(s.id))) scenes.push(String(s.id));
        out.set(id, scenes);
      }
    }
  }
  return out;
}

// -------------------------------------------------------------- étapes

const STEP_ID: Partial<Record<JrafAction, string>> = {
  'object.show': 'montrer',
  'object.hide': 'cacher',
  'object.move': 'deplacer',
  'object.scale': 'echelle',
  'object.rotate': 'tourner',
  'object.style': 'style',
  'label.set': 'texte',
  'formula.transform': 'calcul',
  'value.set': 'valeur',
  'object.highlight': 'eclairer',
  'timeline.wait': 'pause',
  'question.ask': 'question',
  'tool.open': 'outil',
};

/** Modèle d'une étape (la cible : l'objet donné, sinon à choisir). */
export function stepTemplate(action: JrafAction, id: string, target?: string): Json {
  const t = target ?? '';
  switch (action) {
    case 'object.show':
      return { id, action, target: t, effect: 'fade' };
    case 'object.hide':
      return { id, action, target: t, effect: 'fade' };
    case 'object.move':
      return { id, action, target: t, dx: 0, dy: -40 };
    case 'object.scale':
      return { id, action, target: t, scale: 1.2 };
    case 'object.rotate':
      return { id, action, target: t, angle: 90 };
    case 'object.style':
      return { id, action, target: t, fill: 'gold' };
    case 'label.set':
      return { id, action, target: t, text: 'Nouveau texte' };
    case 'formula.transform':
      return { id, action, target: t, to: 'a = b', label: 'Remplacer' };
    case 'value.set':
      return { id, action, target: t, expr: 1 };
    case 'object.highlight':
      return { id, action, target: t };
    case 'timeline.wait':
      return { id, action, durationMs: 1000 };
    case 'question.ask':
      return {
        id,
        action,
        prompt: 'Combien vaut … ?',
        choices: ['1', '2', '3'],
        correct: 0,
        explanation: 'Parce que …',
        exportBehavior: { mode: 'auto-reveal', afterMs: 3000 },
      };
    case 'tool.open':
      return {
        id,
        action,
        toolId: 'pythagoras-squares',
        prompt: 'Vérifie avec l’outil.',
        exportBehavior: { mode: 'cta' },
      };
  }
}

export function addStep(
  draft: unknown,
  sceneId: string,
  action: JrafAction,
  opts: { target?: string; afterIndex?: number } = {},
): { doc: Json; id: string } {
  const doc = asDoc(draft);
  const { scene } = sceneAt(doc, sceneId);
  const steps = Array.isArray(scene.steps) ? (scene.steps as Json[]) : [];
  scene.steps = steps;
  const id = uniqueId(STEP_ID[action] ?? 'etape', idsOf(steps));
  const at = opts.afterIndex === undefined ? steps.length : opts.afterIndex + 1;
  steps.splice(Math.max(0, Math.min(at, steps.length)), 0, stepTemplate(action, id, opts.target));
  return { doc, id };
}

export function moveStep(draft: unknown, sceneId: string, stepId: string, delta: -1 | 1): Json {
  const doc = asDoc(draft);
  const { scene } = sceneAt(doc, sceneId);
  const steps = Array.isArray(scene.steps) ? (scene.steps as Json[]) : [];
  const index = steps.findIndex((s) => s.id === stepId);
  const to = index + delta;
  if (index < 0 || to < 0 || to >= steps.length) return doc;
  const [s] = steps.splice(index, 1);
  steps.splice(to, 0, s!);
  return doc;
}

/** Supprime une étape ; ce qui démarrait `with` elle démarre à sa place. */
export function removeStep(draft: unknown, sceneId: string, stepId: string): Json {
  const doc = asDoc(draft);
  const { scene } = sceneAt(doc, sceneId);
  const steps = (Array.isArray(scene.steps) ? (scene.steps as Json[]) : []).filter(
    (s) => s.id !== stepId,
  );
  for (const s of steps) if (s.with === stepId) delete s.with;
  scene.steps = steps;
  return doc;
}

/** Change l'action d'une étape : garde son identifiant, son ancrage et sa cible. */
export function changeStepAction(
  draft: unknown,
  sceneId: string,
  stepId: string,
  action: JrafAction,
): Json {
  const doc = asDoc(draft);
  const { scene } = sceneAt(doc, sceneId);
  const steps = Array.isArray(scene.steps) ? (scene.steps as Json[]) : [];
  const index = steps.findIndex((s) => s.id === stepId);
  if (index < 0) throw new Error(`étape « ${stepId} » introuvable`);
  const old = steps[index]!;
  const target = typeof old.target === 'string' ? old.target : undefined;
  const next = stepTemplate(action, stepId, target);
  for (const k of ['with', 'atSentence', 'delayMs'] as const)
    if (old[k] !== undefined) next[k] = old[k];
  if (Array.isArray(old.target) && 'target' in next) next.target = old.target;
  steps[index] = next;
  return doc;
}
